<script lang="ts" setup>
import type { FocusTrap } from 'focus-trap'
import { faArrowUp, faClose } from '@fortawesome/pro-regular-svg-icons'
import { useEventListener, useTimeoutFn } from '@vueuse/core'
import { preventWindowScroll } from '@shared/composables'
import GIconButton from '@consumer/components/GIconButton.vue'
import giftlyWatermarkSvg from '@consumer/assets/icons/new-misc/giftly-logo.svg?svgicon'
import ConciergeRecommendations
  from '@consumer/modules/myAccount/shared/recommendations/ConciergeRecommendations.vue'
import ConciergeInputVariant, { type ConciergeInputVariantName } from './ConciergeInputVariant.vue'
import { useConciergeState } from './conciergeState'
import { provideConciergeTakeover } from './useConciergeTakeover'
import { trackConciergeEvent } from './conciergeEventTracking'
import { useConciergeFunnelTracking } from './useConciergeFunnelTracking'

// The input lives inline on the homepage. It expands into a full-screen takeover
// when the buyer is focused in a field that has enough to fetch results — the SAME
// input teleports across (no re-mount, no lost keystrokes). Engagement is driven by
// focus + resultability rather than typing, so refocusing an already-full field
// re-expands without a keystroke. Once open, editing the field down (even to empty)
// keeps it open — the buyer is most likely retyping; only an explicit dismiss or a
// click outside the field while it's empty drops back to the homepage.
// Which input UI to render. Defaults to the free-text variant so nothing changes
// unless a variant is explicitly threaded in (query-param override today, split
// assignment once the UI experiment is registered). Both variants write the same
// store, so everything below here — engagement, teleport, results — is identical.
withDefaults(defineProps<{
  variant?: ConciergeInputVariantName
  // The server's calendar day, for the taps input's seasonal ordering (see BuyersConcierge).
  referenceDate?: Date
}>(), { variant: 'free_text', })

const engaged = ref(false)
const takeover = provideConciergeTakeover(engaged)

useConciergeFunnelTracking(() => __props.variant)

const panel = ref<HTMLElement | null>(null)
const inner = ref<HTMLElement | null>(null)
const inputWrap = ref<HTMLElement | null>(null)

// Everything in the takeover scrolls away — the wordmark, the headline, the input. Once the
// buyer has scrolled past them a "Back to top" badge appears beside the fixed ✕ to jump back.
const scrolled = ref(false)
const BACK_TO_TOP_AFTER = 240
function syncBackToTop () {
  scrolled.value = (panel.value?.scrollTop ?? 0) > BACK_TO_TOP_AFTER
}
useEventListener(panel, 'scroll', syncBackToTop)

function scrollToTop () {
  panel.value?.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
}

// Where the buyer was in the results when the takeover last closed. Reopening — from the
// input or "See all" — lands there again. Forgotten once the prompt is cleared, since those
// results are gone.
let lastScrollTop = 0

// The page scroll where the concierge sits, captured on open. The page is frozen there while the
// takeover is open; on close we restore it, so however far the buyer scrolled inside the takeover
// they land back on the concierge rather than deep down the homepage.
let restoreScrollY = 0

const state = useConciergeState()
watch(() => state.raw, (raw) => {
  if (!raw.trim()) lastScrollTop = 0
})

// True only for the tick an explicit dismiss (✕/Escape) is collapsing, so the focus
// handler drops focus instead of restoring it — restoring would immediately re-open
// a still-ready field.
let dismissing = false

// Open while the buyer is focused in a resultable field. Editing it down — even to
// empty — never closes it: being kicked back to the homepage mid-edit is jarring when
// the buyer is about to retype. Only an explicit dismiss or a click outside the field
// while it's empty closes it (see collapse / onSurfaceClick). Focus leaving doesn't
// close it either — the teleport briefly blurs the input mid-move, and closing on that
// would bounce the takeover straight shut.
//
// Deferred a task, not a tick: a click focuses the input BEFORE the browser places the
// caret where it landed, and Vue flushes in between. Engaging in that gap teleports the
// input away and restores a caret the click hadn't set yet (it reads as 0 — the start of
// the text), so let the click finish first, then re-check.
const { start: scheduleEngage } = useTimeoutFn(() => {
  if (takeover.shouldEngage.value) engaged.value = true
}, 0, { immediate: false })

watch(takeover.shouldEngage, (should) => {
  if (should) scheduleEngage()
})

// An input that empties itself out (a brief with its last detail removed) closes as a
// readiness collapse, not a dismiss: tracked as such, and focus is restored rather than dropped.
watch(takeover.shouldCollapse, (should) => {
  if (should && engaged.value) engaged.value = false
})

function collapse () {
  // No-op when already collapsed: otherwise a stray Escape sets `dismissing` without
  // the engaged watcher ever running to clear it, and it would leak into the next
  // readiness-collapse (blurring instead of restoring caret).
  if (!engaged.value) return
  dismissing = true
  engaged.value = false
}

// Escape closes from anywhere, not only from inside the input: a backdrop click while the
// query still has text keeps the takeover open but blurs the input, and the next Escape
// then fires on <body>, where a handler on the surface never hears it. Escape inside some
// other open dialog is left to that dialog.
useEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return
  const target = event.target
  if (target instanceof Node && target !== document.body && !panel.value?.contains(target)) return
  collapse()
})

// A click on the backdrop — outside the input+results column — while the input reports itself
// dismissable means "I'm done": collapse. Clicks inside the column (retyping, refining a chip,
// paging results) never do. Read the column off the event's propagation path, not the target's
// current ancestors: a control that re-renders away in its own click handler ("See all" swaps
// to "See more", "See more" to skeletons) is detached by the time the click bubbles up here —
// the browser flushes Vue's microtask between listeners — and `closest` on it finds nothing.
function onSurfaceClick (event: MouseEvent) {
  if (!engaged.value || !takeover.dismissOnOutsideClick.value) return
  // The input+results column and the fixed controls (the ✕ and the Back to top badge) are chrome,
  // not the backdrop — a click on either must never dismiss (taps dismisses on any outside click).
  const isChrome = event.composedPath().some(node =>
    node instanceof Element
    && (node.classList.contains('concierge-surface__column')
      || node.classList.contains('concierge-surface__controls')),
  )
  if (isChrome) return
  collapse()
}

// Load focus-trap lazily: it (and tabbable) are only needed once the takeover opens,
// so keeping them out of the statically-bundled island leaves the landing-page payload
// unchanged at rest — and for all of control while the experiment sits at weight 0.
let trap: FocusTrap | null = null
async function activateTrap () {
  if (!panel.value) return
  if (!trap) {
    const { createFocusTrap } = await import('focus-trap')
    if (!panel.value) return
    // We drive focus/blur ourselves (see the engaged watch); the trap must not set
    // initial focus or return focus on deactivate, or dismissing a still-ready field
    // would re-focus the input and immediately re-open the takeover.
    trap = createFocusTrap(panel.value, {
      initialFocus: false,
      escapeDeactivates: false,
      returnFocusOnDeactivate: false,
      allowOutsideClick: true,
    })
  }
  trap.activate()
}

// iOS-safe scroll lock, reactive to `engaged` (the repo primitive GModal et al. use;
// it tears itself down on unmount).
preventWindowScroll({ when: () => engaged.value })

// The input the buyer is actually in — captured generically rather than by id,
// because the madlibs variant is four inputs and the free-text variant one. The
// teleport moves nodes without re-mounting them, so the reference stays valid on
// the other side.
function focusedInput (): HTMLInputElement | null {
  const active = document.activeElement
  return active instanceof HTMLInputElement && panel.value?.contains(active) ? active : null
}

const MOVE_MS = 380

// True while the content glides home after a collapse. The surface is back in the page by
// then, at its resting paint order, where the hero (a Swiper, which layers itself up)
// draws over it — so the glide keeps the dialog layer until it lands.
const collapsing = ref(false)

function prefersReducedMotion (): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// FLIP between the content's two homes: `first` is its rect before the teleport,
// measured pre-patch, and the glide runs from there to wherever it just landed — up
// from the homepage slot into the takeover on open, back into the slot on collapse.
// The white backdrop fades on its own (see the template), in step with the glide.
function animateMove (first: DOMRect): Animation | null {
  const content = inner.value
  if (!content || typeof content.animate !== 'function' || prefersReducedMotion()) return null

  const last = content.getBoundingClientRect()
  const dx = first.left - last.left
  const dy = first.top - last.top
  if (!dx && !dy) return null
  return content.animate(
    [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
    { duration: MOVE_MS, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  )
}

// Teleporting re-parents the input, which blurs it. On open and on a readiness-driven
// collapse, restore focus + the buyer's exact caret to the SAME field they were in, so
// clicking a blank on the homepage lands them typing in that blank inside the takeover.
// On an explicit dismiss, drop focus so a still-ready field doesn't immediately re-open.
watch(engaged, async (open) => {
  const fromSeeAll = open && takeover.consumeOpenRequest()
  if (open) {
    restoreScrollY = window.scrollY
    trackConciergeEvent('takeover_engage', { variant: __props.variant, metadata: { method: fromSeeAll ? 'see_all' : 'input' } })
  }
  else {
    lastScrollTop = panel.value?.scrollTop ?? 0
    scrolled.value = false
    trackConciergeEvent('takeover_collapse', { variant: __props.variant, metadata: { method: dismissing ? 'dismiss' : 'readiness' } })
  }

  const input = focusedInput()
  const start = input?.selectionStart ?? null
  const end = input?.selectionEnd ?? null
  const firstRect = inner.value?.getBoundingClientRect() ?? null

  await nextTick()

  if (open) {
    // Back to where the buyer left the results on reopen. Focus must not scroll it back up, and
    // the glide is skipped from that depth (the content would fly up past the viewport).
    const scrollBack = lastScrollTop
    if (scrollBack && panel.value) panel.value.scrollTop = scrollBack
    const restored = scrollBack > 0
    syncBackToTop()
    // Restore focus + caret before awaiting the lazy trap, so the teleport doesn't
    // leave the input blurred while the focus-trap chunk loads on first open. A "See
    // all" open had no field focused: give it the first one, as a click would.
    if (input) {
      input.focus({ preventScroll: restored })
      if (start !== null && end !== null) input.setSelectionRange(start, end)
    }
    else if (fromSeeAll) {
      inputWrap.value?.querySelector<HTMLElement>('input, button')?.focus({ preventScroll: restored })
    }
    if (firstRect && !restored) animateMove(firstRect)
    await activateTrap()
    return
  }

  trap?.deactivate()
  const wasDismiss = dismissing
  dismissing = false
  if (wasDismiss) {
    if (input && document.activeElement === input) input.blur()
  }
  else if (input) {
    input.focus()
    if (start !== null && end !== null) input.setSelectionRange(start, end)
  }
  // Land the page back on the concierge — undo any scroll that leaked to the window, and override
  // the focus() above, which would otherwise pull the page to the returned field.
  window.scrollTo({ top: restoreScrollY, left: 0, behavior: 'instant' })
  const glide = firstRect ? animateMove(firstRect) : null
  if (glide) {
    collapsing.value = true
    glide.onfinish = glide.oncancel = () => { collapsing.value = false }
  }
})

onBeforeUnmount(() => trap?.deactivate())
</script>

<template>
  <div class="concierge-host">
    <Transition name="concierge-backdrop">
      <div v-if="engaged" class="concierge-backdrop z-dialog">
        <div class="concierge-watermark" aria-hidden="true" v-html="giftlyWatermarkSvg"/>
      </div>
    </Transition>

    <Teleport to="body" :disabled="!engaged">
      <div
        ref="panel"
        class="concierge-surface"
        :class="[
          // hide-window-scrollbar: the page can't scroll under the takeover, so scrolling the
          // results to their end never chains through to it.
          {
            'concierge-surface--engaged': engaged,
            'concierge-surface--collapsing': collapsing,
            'hide-window-scrollbar': engaged,
          },
          engaged || collapsing ? 'z-dialog' : '',
        ]"
        :role="engaged ? 'dialog' : undefined"
        :aria-modal="engaged ? 'true' : undefined"
        :aria-label="engaged ? 'Gift concierge' : undefined"
        @click="onSurfaceClick"
      >
        <div v-if="engaged" class="concierge-surface__controls z-dialog-button">
          <Transition name="concierge-control">
            <GButton
              v-if="scrolled"
              class="concierge-surface__back-to-top"
              :icon="faArrowUp"
              label="Back to top"
              secondary
              pill
              size="small"
              name="concierge-back-to-top"
              @click="scrollToTop"
            />
          </Transition>
          <GIconButton
            :icon="faClose"
            size="medium"
            label="Close gift concierge"
            name="close-buyers-concierge"
            @click="collapse"
          />
        </div>

        <div ref="inner" class="concierge-surface__inner">
          <div class="concierge-surface__column">
            <div v-if="engaged" class="concierge-surface__brand">
              <span class="concierge-brand__name">Giftly Concierge</span>
              <span class="concierge-brand__beta">Beta</span>
            </div>
            <div ref="inputWrap" class="concierge-surface__input">
              <ConciergeInputVariant :variant="variant" :referenceDate="referenceDate"/>
            </div>

            <ConciergeRecommendations :variant="variant"/>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
@use "sass:math";
@use "./conciergeRainbow" as rainbow;

.concierge-host {
  width: 100%;
}

// At rest the surface is a transparent block: the input renders exactly where the page
// places it, no extra chrome. `__inner` stays the landing container's lg width (1240px) in
// both states so the FLIP below measures a constant box; the column inside it decides how
// wide the input and results actually sit.
.concierge-surface__inner {
  margin: 0 auto;
  max-width: 1240px;
  width: 100%;
}

// At rest the input keeps a centred 720px column while the results run the full landing width
// — the same grid and card size as engaged, so opening doesn't reflow them. Capping the input
// — not `__inner` — is what keeps the engage/collapse FLIP (which translates `__inner`) a pure
// translate with no sideways lurch.
.concierge-surface:not(.concierge-surface--engaged) .concierge-surface__input {
  margin-inline: auto;
  max-width: 720px;
}

// At rest the concierge is its own padded band on the homepage (white, no fill): generous space
// above and below so it reads as a distinct section. Steps up on desktop. STARTING VALUES — set to
// the Figma section padding.
.concierge-surface:not(.concierge-surface--engaged) {
  background: var(--color-bg-surface-white);
  padding-block: 48px 32px;
}

@media (min-width: 1024px) {
  .concierge-surface:not(.concierge-surface--engaged) {
    padding-block: 120px 64px;
  }
}

// Engaged: full-screen takeover covering everything, the same input now with room
// below for results. The white is `.concierge-backdrop`, a separate fixed layer, so it
// can still be fading out after this surface has returned to the page on collapse.
// z-layering comes from the z-dialog utility.
.concierge-surface--engaged {
  inset: 0;

  // x hidden (nothing bleeds horizontally now), y auto so the content scrolls.
  overflow: hidden auto;

  // The takeover's scroll never chains out to the frozen page behind it.
  overscroll-behavior: contain;

  // The top spacing lives on `__inner`, not here: everything scrolls now, so there are no
  // sticky children the scroll container's own padding would clip.
  padding: 0 20px 48px;
  position: fixed;

  .concierge-surface__inner {
    // The wordmark is the first scrolling element; keep it near the top as the fixed version was.
    padding-top: 24px;
  }
}

// Positioned so the z-dialog utility applies for the collapse glide (see `collapsing`);
// at rest the surface is a plain block again.
.concierge-surface--collapsing {
  position: relative;
}

// Fades in under the entry glide and out over the collapse glide, so the page fades
// back in as the input returns to its slot instead of snapping.
.concierge-backdrop {
  background: var(--color-bg-surface-white);
  inset: 0;
  position: fixed;
}

.concierge-backdrop-enter-active {
  transition: opacity 0.24s ease-out;
}

.concierge-backdrop-leave-active {
  transition: opacity 0.38s ease-out;
}

.concierge-backdrop-enter-from,
.concierge-backdrop-leave-to {
  opacity: 0;
}

// The controls (✕ and, once scrolled, the Back to top badge) stay pinned to the viewport corner
// while everything else scrolls away. A fixed sibling of `__inner`, not a child: the glide
// translates `__inner`, and a transformed ancestor would drag the controls along. Fades in with
// the glide instead of popping.
.concierge-surface__controls {
  align-items: center;
  animation: concierge-close-fade 0.38s ease-out;
  display: flex;
  gap: 12px;
  position: fixed;
  right: 20px;
  top: 20px;
}

@keyframes concierge-close-fade {
  from {
    opacity: 0;
  }
}

// The Back to top badge fades as the buyer crosses the scroll threshold.
.concierge-control-enter-active,
.concierge-control-leave-active {
  transition: opacity 0.2s ease;
}

.concierge-control-enter-from,
.concierge-control-leave-to {
  opacity: 0;
}

// Shared takeover chrome (every variant): the wordmark + Beta sit at the top of the scrolling
// content, above the headline, so they scroll away with everything else. Engaged-only.
.concierge-surface__brand {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: center;

  // Generous, responsive gap under the wordmark (the vh rhythm the engaged content had before),
  // so the headline sits where the mock places it while the wordmark stays at the top.
  margin-bottom: 4vh;
}

// Wordmark rainbow knobs: $wordmark-colors-zoom = how much of the rainbow shows at once (higher =
// fewer colours; 400% ≈ two), the animation duration = seconds for one full rainbow to travel.
$wordmark-colors-zoom: 400%;

// Pan exactly one rainbow width at this zoom, so one cycle = one rainbow and it loops seamlessly.
$wordmark-pan-end: math.div($wordmark-colors-zoom, $wordmark-colors-zoom - 100%) * 100%;

.concierge-brand__name {
  animation: concierge-wordmark-pan 24s linear infinite;
  background: rainbow.$gradient;
  background-clip: text;
  background-size: $wordmark-colors-zoom 100%;
  color: transparent;
  font-size: 1.125rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

@keyframes concierge-wordmark-pan {
  to {
    background-position: $wordmark-pan-end 0;
  }
}

// A quiet neutral pill — deliberately not the gradient, so Beta reads as a status rather than
// part of the wordmark.
.concierge-brand__beta {
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
  color: var(--color-text-tertiary);
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 3px 9px;
}

// The Giftly script behind the takeover, shared by every variant. It rides in the backdrop
// layer so the teleported input and results paint over it and it fades with the backdrop.
// Decorative only (aria-hidden). Size / opacity / position are visual knobs.
.concierge-watermark {
  color: var(--color-text-primary-dark);
  left: 50%;
  opacity: 0.04;
  pointer-events: none;
  position: absolute;
  top: 62%;
  transform: translate(-50%, -50%);
  width: min(120vw, 1040px);
}

// The injected markup has no scope attribute, so reach it with :deep.
.concierge-watermark :deep(svg) {
  display: block;
  height: auto;
  width: 100%;
}

@media (min-width: 1024px) {
  .concierge-surface__brand {
    margin-bottom: 12vh;
  }
}

@media (prefers-reduced-motion: reduce) {
  .concierge-surface__controls,
  .concierge-brand__name {
    animation: none;
  }

  .concierge-backdrop-enter-active,
  .concierge-backdrop-leave-active,
  .concierge-control-enter-active,
  .concierge-control-leave-active {
    transition: none;
  }
}

</style>
